import { createApp } from 'vue'
import { createPinia } from 'pinia'
import ElementPlus from 'element-plus'
import 'element-plus/dist/index.css'
import 'element-plus/theme-chalk/dark/css-vars.css'
import * as ElementPlusIconsVue from '@element-plus/icons-vue'
import App from './App.vue'
import router from './router'
import i18n, { initI18n } from './i18n'
import './styles/index.scss'
import devConfig from '@/config/dev.config'
import { useAuthStore } from '@/stores/auth'
import { installGlobalErrorHandler } from '@/plugins/errorHandler'

// 应用初始化函数
//
// T-138：**导出**（原为模块内私有）—— 让测试能对启动链结构做断言（本项目**没有 jsdom**，
// 且本文件会经 `src/router/index.ts` 的 `createWebHistory()` 触碰 `window` ⇒ 在 node 环境下
// **无法 import**，实测 `ReferenceError: window is not defined`；钉子落在
// `src/main.startup.spec.ts` 的「结构守卫 ＋ 可执行模型」上）。
// ⚠️ 导出不改变运行行为：文件末尾仍照旧立即调用一次 `initApp()`。
export async function initApp() {
  // 条件加载 Mock: 根据配置启用
  if (devConfig.enableMock) {
    await import('@/mock')
    console.log('[App] Mock API enabled - 使用模拟数据')
  }

  const app = createApp(App)
  const pinia = createPinia()

  app.use(pinia)
  app.use(router)
  app.use(i18n)
  app.use(ElementPlus)

  installGlobalErrorHandler(app, router)

  for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
    app.component(key, component)
  }

  // Initialize i18n by loading locale JSON files, then mount app
  //
  // T-138：`initI18n()` 内部的 locale `fetch` 现已有**超时 ＋ 中止**（`fetchWithTimeout`），
  // 故「网络挂起」会变成一次确定的 reject ⇒ **这里的 catch 一定会被执行到**（旧行为是
  // 「既不 resolve 也不 reject」⇒ try/catch 两个分支都不执行 ⇒ mount 永不发生 ⇒ 永久白屏）。
  // 两个分支都 mount：**失败可见（console.error）但不阻断启动**。
  try {
    await initI18n()
    const authStore = useAuthStore(pinia)
    authStore.initAuth()
    app.mount('#app')
  } catch (error) {
    console.error('Failed to initialize i18n:', error)
    // Mount anyway with fallback empty messages
    const authStore = useAuthStore(pinia)
    authStore.initAuth()
    app.mount('#app')
  }
}

// 启动应用
initApp()
